import { IconExclamationCircle } from '@tabler/icons-react';
import { Modal, Skeleton, Text } from '@mantine/core';
import { useLogoutFunction } from '@propelauth/react';
import { api } from '~/trpc-extra/api';

export function SubscriptionSuspendedModal() {
  const signOut = useLogoutFunction();

  const { data: failedInvoiceData, isLoading: isLoadingFailedInvoice } = api.stripe.getFailedInvoice.useQuery();

  const invoice = failedInvoiceData?.invoice;
  const customerPortalUrl = failedInvoiceData?.customerPortalUrl;
  const hasMultipleOpenInvoices = failedInvoiceData?.hasMultipleOpenInvoices ?? false;
  const openInvoiceCount = failedInvoiceData?.openInvoiceCount ?? 0;
  const formattedAmount =
    invoice ?
      new Intl.NumberFormat('en-US', { style: 'currency', currency: invoice.currency }).format(invoice.amountDue / 100)
    : null;

  return (
    <Modal
      opened={true}
      onClose={() => {}}
      withCloseButton={false}
      closeOnEscape={false}
      closeOnClickOutside={false}
      size="md"
      centered
      padding={0}
      radius="lg"
      styles={{
        content: {
          overflow: 'hidden',
          backgroundColor: 'transparent',
        },
        body: {
          padding: 0,
        },
      }}
      className="[&_.mantine-Modal-content]:bg-white dark:[&_.mantine-Modal-content]:bg-zinc-950"
    >
      <div className="flex flex-col rounded-lg bg-white dark:bg-zinc-950">
        {/* Header */}
        <div className="border-b border-zinc-200 px-8 py-6 text-center dark:border-zinc-800">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
            <IconExclamationCircle className="h-6 w-6 text-red-600 dark:text-red-400" />
          </div>
          <h2 className="mb-1 text-xl font-bold text-zinc-950 dark:text-zinc-50">Your subscription is suspended</h2>
          <p className="text-sm text-zinc-500 dark:text-zinc-400">
            {hasMultipleOpenInvoices ?
              `You have ${openInvoiceCount} unpaid invoices that need to be resolved.`
            : formattedAmount ?
              `You have an unpaid invoice of ${formattedAmount}.`
            : 'You have an outstanding invoice that needs to be resolved.'}
          </p>
        </div>

        {/* Body */}
        <div className="px-8 py-6">
          {isLoadingFailedInvoice ?
            <div className="flex flex-col gap-3">
              <Skeleton
                height={16}
                width="80%"
                radius="sm"
              />
              <Skeleton
                height={40}
                width="100%"
                radius="sm"
              />
            </div>
          : <div className="flex flex-col gap-4">
              <Text
                size="sm"
                c="dimmed"
              >
                {hasMultipleOpenInvoices ?
                  'Please review and pay your unpaid invoices in the billing portal to restore access. Fresh checkout is not available while invoices remain unpaid.'
                : 'Please resolve your outstanding invoice to restore access. Fresh checkout is not available while an invoice is unpaid.'
                }
              </Text>
              <div className="flex flex-col gap-2">
                {!hasMultipleOpenInvoices && invoice?.hostedInvoiceUrl && (
                  <a
                    href={invoice.hostedInvoiceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex h-11 w-full items-center justify-center rounded-md bg-zinc-900 px-8 text-sm font-medium text-zinc-50 shadow transition-colors hover:bg-zinc-900/90 dark:bg-zinc-50 dark:text-zinc-900 dark:hover:bg-zinc-50/90"
                  >
                    Pay Now to Reactivate
                  </a>
                )}
                {customerPortalUrl && (
                  <a
                    href={customerPortalUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex h-10 w-full items-center justify-center rounded-md border border-zinc-200 px-8 text-sm font-medium text-zinc-700 transition-colors hover:bg-zinc-50 dark:border-zinc-800 dark:text-zinc-300 dark:hover:bg-zinc-900"
                  >
                    Update Payment Method
                  </a>
                )}
              </div>
            </div>
          }
        </div>

        {/* Footer */}
        <div className="border-t border-zinc-200 px-8 py-4 text-center dark:border-zinc-800">
          <button
            onClick={async () => {
              await signOut(true);
            }}
            className="text-sm text-zinc-500 transition-colors hover:text-zinc-900 hover:underline hover:underline-offset-4 dark:text-zinc-400 dark:hover:text-zinc-50"
          >
            Switch Account
          </button>
        </div>
      </div>
    </Modal>
  );
}
